// src/components/Timeline/hooks/useImageViewer.ts
import { useState, useCallback, useEffect } from 'react';

// Add Image interface
interface Image {
  id: string;
  url: string;
  title?: string;
  description?: string;
  location?: string;
  date?: string;
  tags?: string[];
  uploadedBy?: {
    id: string;
    name: string;
    avatar?: string;
  };
}

/**
 * Custom hook to handle image viewing functionality
 * 
 * @returns Image viewing state and handlers
 */
export function useImageViewer() {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [currentImages, setCurrentImages] = useState<(string | Image)[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Reset loading state when image changes
  useEffect(() => {
    if (imageDialogOpen) {
      setLoading(true);
    }
  }, [currentImageIndex, imageDialogOpen]);
  
  // Handle keyboard controls
  useEffect(() => {
    if (!imageDialogOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
          handleNextImage();
          break;
        case 'ArrowLeft':
          handlePrevImage();
          break;
        case 'Escape':
          closeImageDialog();
          break;
        case '+':
          setZoomLevel(prev => Math.min(prev + 0.25, 3));
          break;
        case '-':
          setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
          break;
        case 'f':
          toggleFullscreen();
          break;
        case '0':
          setZoomLevel(1);
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [imageDialogOpen, currentImages]);
  
  /**
   * Open the image dialog with specified images and starting index
   */
  const openImageDialog = useCallback((images: (string | Image)[], startIndex: number = 0) => {
    setCurrentImages(images);
    setCurrentImageIndex(startIndex);
    setZoomLevel(1);
    setImageDialogOpen(true);
  }, []);
  
  /**
   * Close the image dialog
   */
  const closeImageDialog = useCallback(() => {
    setImageDialogOpen(false);
    // Exit fullscreen if active
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => {
        console.error(`Error exiting fullscreen: ${err.message}`);
      });
    }
  }, []);
  
  /**
   * Navigate to the next image
   */
  const handleNextImage = useCallback(() => {
    if (currentImages.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % currentImages.length);
  }, [currentImages.length]);
  
  /**
   * Navigate to the previous image
   */
  const handlePrevImage = useCallback(() => {
    if (currentImages.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + currentImages.length) % currentImages.length);
  }, [currentImages.length]);
  
  /**
   * Toggle fullscreen mode
   */
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().then(() => {
          setIsFullscreen(true);
        }).catch(err => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        }).catch(err => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }
  }, []);
  
  /**
   * Handle image load event
   */
  const handleImageLoad = useCallback(() => {
    setLoading(false);
  }, []);
  
  /**
   * Handle zoom in
   */
  const zoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  }, []);
  
  /**
   * Handle zoom out
   */
  const zoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  }, []);
  
  /**
   * Reset zoom level
   */
  const resetZoom = useCallback(() => {
    setZoomLevel(1);
  }, []);
  
  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  return {
    // State
    imageDialogOpen,
    currentImages,
    currentImageIndex,
    zoomLevel,
    isFullscreen,
    loading,
    
    // Image navigation
    openImageDialog,
    closeImageDialog,
    handleNextImage,
    handlePrevImage,
    
    // Zoom controls
    zoomIn,
    zoomOut,
    resetZoom,
    
    // Fullscreen
    toggleFullscreen,
    
    // Loading
    handleImageLoad
  };
}

export default useImageViewer;